import React from 'react';
import styled from 'styled-components';
import { fadeIn } from '../../Themes/Animations';
import { get } from 'lodash';

type PopoverProps = {
  content: React.ReactElement;
  showOnMoseOver?: boolean;
  defaultShowState?: boolean;
};

type PopoverState = {
  displayPopover: boolean;
};

class Popover extends React.Component<PopoverProps, PopoverState> {
  popoverRef = React.createRef<HTMLDivElement>();

  constructor(props: PopoverProps) {
    super(props);

    const { defaultShowState = false } = props;

    this.state = {
      displayPopover: defaultShowState,
    };
  }

  componentDidUpdate(prevProps: Readonly<PopoverProps>): void {
    const { defaultShowState: prevDefaultShowState } = prevProps;
    const { defaultShowState } = this.props;

    if (defaultShowState !== prevDefaultShowState) {
      this.setState({ displayPopover: !!defaultShowState });
    }
  }

  hidePopover = (): void => this.setState({ displayPopover: false });

  showPopover = (): void => this.setState({ displayPopover: true });

  togglePopover = (): void =>
    this.setState((state) => ({
      displayPopover: !state.displayPopover,
    }));

  handleClickOutside = (event: Event): void => {
    const { displayPopover } = this.state;
    const current = get(this.popoverRef, 'current');
    const target = get(event, 'target');

    if (displayPopover && current && !current.contains(target as Node | null)) {
      this.hidePopover();
    }
  };

  componentDidMount(): void {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  render(): JSX.Element {
    const { content, children, showOnMoseOver = false } = this.props;
    const { displayPopover } = this.state;

    return (
      <PopoverContainer
        onMouseLeave={showOnMoseOver ? this.hidePopover : undefined}
      >
        {displayPopover && (
          <div className={`tooltip-bubble popover`} ref={this.popoverRef}>
            <div className="popover-message">{content}</div>
          </div>
        )}
        <span
          className="popover-trigger"
          onMouseOver={showOnMoseOver ? this.showPopover : undefined}
          onClick={this.togglePopover}
        >
          {children}
        </span>
      </PopoverContainer>
    );
  }
}

export default Popover;

const PopoverContainer = styled.div`
  display: inline-block;
  position: relative;

  .popover-trigger {
    display: inline-block;
    text-decoration: underline;
  }

  .popover-bubble {
    position: absolute;
    z-index: 10;
    &::after {
      content: '';
      position: absolute;
    }
  }

  .popover {
    top: 30px;
    transform: translateX(calc(-100% + 70px));
    position: absolute;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.15);
    z-index: 100;

    &::after {
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      border-bottom: 9px solid white;
      top: 0;
      left: 50%;
    }
  }

  .popover-message {
    background: ${({ theme }) => theme.UI_COLORS.light};
    border-radius: 8px;
    color: ${({ theme }) => theme.UI_COLORS.secondary};
    font-size: 0.75rem;
    line-height: 1.4;
    animation: ${fadeIn} 0.5s;

    &::before {
      content: '';
      background: ${({ theme }) => theme.UI_COLORS.light};
      width: 10px;
      right: 17px;
      top: -5px;
      height: 10px;
      transform: rotate(45deg);
      position: absolute;
    }
  }
`;
